'use client';

import { Menu, MenuItem, Divider, Popper, Paper, MenuList, Grow } from '@mui/material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Link from 'next/link';
import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { RouteConfig, routes } from '@/config/routeConfig';
import { useSystemConfig } from '@/hooks/useSystemConfig';

interface NavigationMenuProps {
  anchorEl: null | HTMLElement; // Element that triggers the menu (e.g., button)
  onClose: () => void; // Function to close the menu
}

export default function NavigationMenu({ anchorEl, onClose }: NavigationMenuProps) {
  const { isAuthorized } = useAuthStore();
  // Track which submenus are currently open
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  // Store anchor elements for submenu positioning (using state to avoid ref access during render)
  const [menuItemAnchors, setMenuItemAnchors] = useState<{ [key: string]: HTMLElement | null }>({});

  const systemConfig = useSystemConfig();
  // Return null if config is not initialized
  if (!systemConfig) {
    return null;
  }

  // Handle mouse enter on menu items with submenus
  const handleMouseEnter = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: true }));
  };

  // Handle mouse leave on menu items with submenus
  const handleMouseLeave = (label: string) => {
    setOpenMenus((prev) => ({ ...prev, [label]: false }));
  };

  // Filter routes based on user privileges only (parent and children independently)
  const filterRoutesByPrivilege = (routes: RouteConfig[]): RouteConfig[] => {
    return routes.filter((route) => {
      return !route.privileges || route.privileges.some((p) => isAuthorized(p.p, p.op ?? 'read'));
    });
  };

  const authorizedRoutes = filterRoutesByPrivilege(routes);

  return (
    <Menu
      id="nav-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      {authorizedRoutes.map((item, index) => {
        // Show divider if specified and not the first item
        const showDivider = item.dividerBefore && index !== 0;

        return (
          <div key={item.label}>
            {showDivider ? <Divider sx={{ my: 1 }} /> : null}
            {item.subRoutes ? (
              // Menu item with submenu
              <MenuItem
                ref={(el) => {
                  if (el) {
                    setMenuItemAnchors((prev) => {
                      // Only update if the element has actually changed
                      if (prev[item.label] === el) {
                        return prev;
                      }
                      return { ...prev, [item.label]: el };
                    });
                  }
                }}
                onMouseEnter={() => handleMouseEnter(item.label)}
                onMouseLeave={() => handleMouseLeave(item.label)}
                component="li"
              >
                {item.label}
                <ChevronRight style={{ marginLeft: 'auto' }} />
                {/* Submenu using Popper for better positioning */}
                <Popper
                  open={Boolean(openMenus[item.label])}
                  anchorEl={menuItemAnchors[item.label] || null}
                  placement="right-start"
                  transition
                  sx={{ zIndex: 1301 }} // Ensure submenu appears above parent menu
                >
                  {({ TransitionProps }) => (
                    <Grow {...TransitionProps}>
                      <Paper>
                        <MenuList>
                          {filterRoutesByPrivilege(item.subRoutes || []).map((subItem) => (
                            <MenuItem
                              key={subItem.path}
                              onClick={onClose}
                              component={Link}
                              href={systemConfig.basePath + subItem.path}
                            >
                              {subItem.label}
                            </MenuItem>
                          ))}
                        </MenuList>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </MenuItem>
            ) : (
              // Regular menu item without submenu
              <MenuItem onClick={onClose} component={Link} href={systemConfig.basePath + item.path}>
                {item.label}
              </MenuItem>
            )}
          </div>
        );
      })}
    </Menu>
  );
}
